import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import useConfig from "../../actions/Tenants/config/configHook";
import { RootState } from "../../reducers";

// First, define the Option interface
export interface Option {
    id: string;
    value: string;
    label: string;
}
export interface TenantDefect extends Option {
    defect_id: string;
    position: string;
    severity: string;
}

export const removeDuplicates = (meta, fieldName = "value") => {
    return meta.filter((item, index, array) => {
        return array.findIndex((i) => i[fieldName] === item[fieldName]) === index;
    });
};

export const doFilterDefects = (object, options) => {
    // First, filter the options based on the given criteria.
    return options
        .filter((i) => !object.defect_position || !i.position || object.defect_position.includes(i.position))
        .filter((i) => !object.defect_severity || !i.severity || object.defect_severity.includes(i.severity))
        .map((i) => ({ ...i, value: i.defect_id }));
};


export function useDefectsUtil() {
    const allDefects = useSelector((state: RootState) => state?.tenants?.defects_by_fruit_type);
    const { tenant_id } = useConfig();
    const defectsLoaded = Object.prototype.hasOwnProperty.call(allDefects || {}, tenant_id);
    const getDefectsForFruitType = useCallback((fruit_type: string | string[]): TenantDefect[] => {
        const types = Array.isArray(fruit_type) ? fruit_type : [fruit_type];
        const defects = types.flatMap((type) => allDefects?.[tenant_id]?.[type] || []);
        return removeDuplicates(defects, "defect_id");
    }, [allDefects, tenant_id]);

    return { getDefectsForFruitType, defectsLoaded };

}

export function useTenantDefectDefinitionsState(fruit_type: string | string[]) {
    const { getDefectsForFruitType, defectsLoaded } = useDefectsUtil();
    const definitions = useMemo(() => getDefectsForFruitType(fruit_type), [getDefectsForFruitType, fruit_type]);
    return { definitions, isLoaded: defectsLoaded };
}

export function useTenantDefectDefinitions(fruit_type: string | string[]): TenantDefect[] {
    return useTenantDefectDefinitionsState(fruit_type).definitions;
}


export function useManagedOptionsHook({ object, name }): Option[] {
    const allOptions = useSelector((state: RootState) => state?.tenants?.options_by_field);
    const { tenant_id } = useConfig();

    // Destructure for clearer dependencies
    const { supplier, country, fruit_type, global_gap_number } = object;

    const filterOptions = useMemo(() => {
        const options = allOptions?.[tenant_id]?.[name] || [];
        if (!allOptions || options.length === 0) return [];

        // Filter the options based on the given criteria
        const filteredOptions = options
            // we don't need to filter clients by country
            .filter((i) => i.field === "end_client" || (!country || !i.country || country.includes(i.country)))
            .filter((i) => {
                if (i.field === "grower") {
                    return !supplier || supplier.includes(i.supplier);
                }
                return !supplier || !i.supplier || supplier.includes(i.supplier);
            })
            .filter((i) => !fruit_type || !i.fruit_type || i.fruit_type === fruit_type);

        // Sort the filtered options
        // Prioritize options based on their label, but ensure that "Other" or options with value "other" are at the end
        const sortedOptions = filteredOptions.sort((a, b) => {
            const isOtherA = (a.label?.toLowerCase() || a.value?.toLowerCase()) === "other";
            const isOtherB = (b.label?.toLowerCase() || b.value?.toLowerCase()) === "other";

            if (isOtherA && !isOtherB) return 1; // Move 'a' to the end if it's "Other"
            if (!isOtherA && isOtherB) return -1; // Move 'b' to the end if it's "Other"

            // If neither 'a' nor 'b' is "Other", or both are, then sort alphabetically by label
            return (a.label || "").localeCompare(b.label || "");
        });

        return removeDuplicates(sortedOptions);
    }, [allOptions, tenant_id, name, supplier, country, fruit_type, global_gap_number]);

    return filterOptions;
}
